import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // Увеличен до 30 минут для очень больших архивов
  headers: {
    'Content-Type': 'application/json'
  }
})

// Добавляем токен авторизации к каждому запросу
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token')
    const hasValidToken = token && token !== 'null' && token !== 'undefined'
    if (hasValidToken) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      // На всякий случай убираем заголовок, если токен невалиден
      if (config.headers && config.headers.Authorization) {
        delete config.headers.Authorization
      }
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Перехватчик ошибок и обработка 401/403
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error)
    
    // Если 401 или 403 - токен отсутствует/недействителен, перенаправляем на логин
    if ((error.response?.status === 401 || error.response?.status === 403) && window.location.pathname !== '/login') {
      localStorage.removeItem('access_token')
      localStorage.removeItem('user_info')
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

export default {
  // Универсальные HTTP методы для прямого использования
  get(url, config = {}) {
    return api.get(url, config)
  },

  post(url, data = null, config = {}) {
    return api.post(url, data, config)
  },

  put(url, data = null, config = {}) {
    return api.put(url, data, config)
  },

  delete(url, config = {}) {
    return api.delete(url, config)
  },

  // Авторизация
  async login(credentials) {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me')
    return response.data
  },

  async logout() {
    const response = await api.post('/auth/logout')
    return response.data
  },

  // Продукты
  getProducts(params = {}) {
    return api.get('/products', { params })
  },

  getFilterOptions() {
    return api.get('/products/filter-options')
  },

  createProduct(product) {
    return api.post('/products', product)
  },

  updateProduct(id, product) {
    return api.put(`/products/${id}`, product)
  },

  deleteProduct(id) {
    return api.delete(`/products/${id}`)
  },

  // Линзы (параллельный каталог)
  getLenses(params = {}) {
    return api.get('/lenses', { params })
  },

  getLensFilterOptions() {
    return api.get('/lenses/filter-options')
  },

  createLens(lens) {
    return api.post('/lenses', lens)
  },

  updateLens(id, lens) {
    return api.put(`/lenses/${id}`, lens)
  },

  deleteLens(id) {
    return api.delete(`/lenses/${id}`)
  },

  uploadLenses(file) {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/lenses/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Состояние прокси (канал для AI-функций)
  getProxyStatus(force = false) {
    return api.get('/system/proxy-status', { params: force ? { force: true } : {} })
  },

  // Чат
  sendChatQuery(query, history = []) {
    return api.post('/chat/query', { query, history })
  },

  // История чата (серверная)
  getChatHistory(limit = 10) {
    return api.get('/chat/history', { params: { limit } })
  },

  addChatMessage(message) {
    // message: { role: 'user'|'ai', text?: string, sql?: string, error?: string }
    return api.post('/chat/history', message)
  },

  clearChatHistory() {
    return api.delete('/chat/history')
  },

  // Загрузка файлов
  uploadExcel(file) {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/upload/excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  confirmMapping(fileOrToken, mapping, valueMappings = {}, locale = 'ru', autoGenerateDescriptions = true, protectedFields = []) {
    const formData = new FormData()
    
    // Поддержка двух режимов: file_token (новый) или file (старый)
    if (typeof fileOrToken === 'string') {
      // Это токен
      formData.append('file_token', fileOrToken)
    } else {
      // Это файл (обратная совместимость)
      formData.append('file', fileOrToken)
    }
    
    formData.append(
      'mapping_data',
      JSON.stringify({
        mapping,
        value_mappings: valueMappings,
        locale,
        protected_fields: protectedFields  // Добавляем защищённые поля
      })
    )
    formData.append('auto_generate_descriptions', autoGenerateDescriptions)
    
    return api.post('/upload/confirm-mapping', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  downloadRejectedRows(fileToken) {
    return api.get(`/upload/download-rejected/${fileToken}`, {
      responseType: 'blob'
    })
  },

  previewMapping(fileOrToken, mapping, valueMappings = {}, locale = 'ru') {
    const formData = new FormData()
    
    // Поддержка двух режимов: file_token (новый) или file (старый)
    if (typeof fileOrToken === 'string') {
      // Это токен
      formData.append('file_token', fileOrToken)
    } else {
      // Это файл (обратная совместимость)
      formData.append('file', fileOrToken)
    }
    
    formData.append(
      'mapping_data',
      JSON.stringify({
        mapping,
        value_mappings: valueMappings,
        locale
      })
    )
    
    return api.post('/upload/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  // Экспорт данных
  exportProducts(exportData) {
    return api.post('/products/export', exportData, { responseType: 'blob' })
  },

  // Бренд → производитель
  async importBrandProducers(file, overwrite = false) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/brands/producers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { overwrite }
    })
    return response.data
  },

  async getBrandProducers() {
    const response = await api.get('/brands/producers')
    return response.data
  },

  // Накладные (вознаграждения)
  getRewardInvoices(params = {}) {
    return api.get('/rewards/invoices', { params })
  },

  getRewardInvoiceDetail(invoiceId) {
    return api.get(`/rewards/invoices/${invoiceId}`)
  },

  // Получение колонок БД
  getProductColumns() {
    return api.get('/products/columns')
  },

  // Ручной запуск синхронизации прайс-листа
  syncProducers() {
    return api.post('/products/sync/producers')
  },

  syncPriceFeed() {
    return api.post('/products/sync/price')
  },

  syncImages(params = {}) {
    return api.post('/products/sync/images', null, { params })
  },

  syncConditions() {
    return api.post('/products/sync/conditions')
  },

  regenerateDescriptions(params = {}) {
    // params может содержать: { brand_cp: ['brand1', 'brand2'], only_without_descriptions: true }
    return api.post('/products/regenerate-descriptions', null, { params })
  },

  // Настройки маппинга
  getMappingSettings() {
    return api.get('/mapping-settings').then(response => response.data)
  },

  createMappingSetting(data) {
    return api.post('/mapping-settings', data).then(response => response.data)
  },

  updateMappingSetting(id, data) {
    return api.put(`/mapping-settings/${id}`, data).then(response => response.data)
  },

  deleteMappingSetting(id) {
    return api.delete(`/mapping-settings/${id}`).then(response => response.data)
  },

  // Исключения состояния — переопределение колонки condition по бренду/производителю
  getConditionExceptions() {
    return api.get('/condition-exceptions').then(response => response.data)
  },

  createConditionException(data) {
    return api.post('/condition-exceptions', data).then(response => response.data)
  },

  updateConditionException(id, data) {
    return api.put(`/condition-exceptions/${id}`, data).then(response => response.data)
  },

  deleteConditionException(id) {
    return api.delete(`/condition-exceptions/${id}`).then(response => response.data)
  },

  applyConditionExceptions() {
    return api.post('/condition-exceptions/apply').then(response => response.data)
  },

  // Словарь «Статус на сайте → код состояния (0..4)»
  getSiteStatusMappings() {
    return api.get('/site-status-mappings').then(response => response.data)
  },

  getUnmappedSiteStatuses() {
    return api.get('/site-status-mappings/unmapped').then(response => response.data)
  },

  createSiteStatusMapping(data) {
    return api.post('/site-status-mappings', data).then(response => response.data)
  },

  updateSiteStatusMapping(id, data) {
    return api.put(`/site-status-mappings/${id}`, data).then(response => response.data)
  },

  deleteSiteStatusMapping(id) {
    return api.delete(`/site-status-mappings/${id}`).then(response => response.data)
  },

  applySiteStatusMappings() {
    return api.post('/site-status-mappings/apply').then(response => response.data)
  },

  // Правила маппинга бренда (alias / prefix) для авто-определения при импорте
  getBrandMappingRules() {
    return api.get('/brand-mapping').then(response => response.data)
  },

  createBrandMappingRule(data) {
    return api.post('/brand-mapping', data).then(response => response.data)
  },

  updateBrandMappingRule(id, data) {
    return api.put(`/brand-mapping/${id}`, data).then(response => response.data)
  },

  deleteBrandMappingRule(id) {
    return api.delete(`/brand-mapping/${id}`).then(response => response.data)
  },

  // Управление изображениями и папками
  getImagesAndFolders(path = '/', options = {}, signal = null) {
    const params = { path }
    if (options.limit != null) params.limit = options.limit
    if (options.cursor) params.cursor = options.cursor
    if (options.token && !options.cursor) params.token = options.token
    if (options.query) params.query = options.query
    
    const config = { params }
    if (signal) config.signal = signal
    
    return api.get('/images/browse', config).then(response => response.data)
  },

  createFolder(parentPath, name) {
    return api.post('/images/folders', { parentPath, name }).then(response => response.data)
  },

  deleteFolder(folderPath) {
    return api.delete('/images/folders', { data: { path: folderPath } }).then(response => response.data)
  },

  renameFolder(oldPath, newName) {
    return api.put('/images/folders/rename', { oldPath, newName }).then(response => response.data)
  },

  uploadImagesToFolder(folderPath, images) {
    const formData = new FormData()
    formData.append('folderPath', folderPath)
    images.forEach((image) => {
      formData.append(`images`, image)
    })
    return api.post('/images/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(response => {
      return response.data
    }).catch(error => {
      console.error('Upload error:', error)
      throw error
    })
  },

  async uploadImagesToFolderChunked(folderPath, images, onProgress) {
    const CHUNK_SIZE = 10
    const results = { uploaded: [], failed: [], replaced_duplicates: 0 }
    for (let i = 0; i < images.length; i += CHUNK_SIZE) {
      const chunk = images.slice(i, i + CHUNK_SIZE)
      const formData = new FormData()
      formData.append('folderPath', folderPath)
      chunk.forEach(img => formData.append('images', img))
      const resp = await api.post('/images/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            const chunkProgress = (i + (e.loaded / e.total) * chunk.length) / images.length * 100
            onProgress(Math.min(chunkProgress, 100))
          }
        }
      })
      const data = resp.data
      results.uploaded.push(...(data.uploaded || []))
      results.failed.push(...(data.failed || []))
      results.replaced_duplicates += data.replaced_duplicates || 0
    }
    results.message = `Загружено ${results.uploaded.length} из ${images.length} файлов`
    if (results.replaced_duplicates > 0) {
      results.message += ` (${results.replaced_duplicates} дубликатов заменено)`
    }
    return results
  },

  uploadZipToFolder(folderPath, zipFile, uploadId = null, forceOverwrite = false) {
    const formData = new FormData()
    formData.append('folderPath', folderPath)
    formData.append('zipFile', zipFile)
    if (uploadId) {
      formData.append('uploadId', uploadId)
    }
    if (forceOverwrite) {
      formData.append('forceOverwrite', 'true')
    }
    return api.post('/images/upload-zip', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(response => response.data)
  },

  initUploadProgress(uploadId, filename, sizeMb) {
    return api.post('/images/upload-progress/init', { uploadId, filename, sizeMb }).then(r => r.data)
  },

  getUploadProgress(uploadId) {
    return api.get(`/images/upload-progress/${uploadId}`).then(response => response.data)
  },

  deleteImage(imagePath) {
    return api.delete('/images/delete', { data: { path: imagePath } }).then(response => response.data)
  },

  renameImage(oldPath, newName) {
    return api.put('/images/rename', { oldPath, newName }).then(response => response.data)
  },

  // Множественные операции с изображениями
  bulkDeleteItems(paths) {
    return api.delete('/images/bulk-delete', { data: { paths } }).then(response => response.data)
  },

  moveItems(sourcePaths, targetPath) {
    return api.post('/images/move-items', { sourcePaths, targetPath }).then(response => response.data)
  },

  resyncImageFolders(payload) {
    return api.post('/images/resync-folders', payload).then(response => response.data)
  },

  exportImages(paths, archiveName = null) {
    return api.post(
      '/images/export',
      { paths, archiveName },
      { responseType: 'blob' }
    ).then(response => response.data)
  },

  createDefaultMappingSettings() {
    return api.post('/mapping-settings/bulk-create').then(response => response.data)
  },

  // Конвертер UPC в ТНВЭД
  convertUpcToTnved(data) {
    return api.post('/convert-upc-to-tnved', data)
  },

  // Восстановление базы данных
  restoreDatabase() {
    return api.post('/restore-database')
  },

  // Обогащение данных товаров через Perplexity
  enrichProducts(brands = null, fields = null) {
    return api.post('/enrich/products', {
      brands: brands,
      fields: fields
    }).then(response => response.data)
  },

  getEnrichableFields() {
    return api.get('/enrich/available-fields').then(response => response.data)
  },

  // ============================================================================
  // Магазины (Stores)
  // ============================================================================

  // Получить список магазинов
  getStores() {
    return api.get('/stores').then(response => response.data)
  },

  // Получить детальную информацию о магазине (только для админов)
  getStoreDetails(storeId) {
    return api.get(`/stores/${storeId}`).then(response => response.data)
  },

  // ============================================================================
  // Синхронизация товаров по брендам
  // ============================================================================

  // Получить список брендов активных товаров для синхронизации
  getBrandsForSync(storeId) {
    return api.get('/sync/products/brands', {
      params: { store_id: storeId }
    }).then(response => response.data)
  },

  // Синхронизация товаров по выбранным брендам
  syncProductsByBrands(storeId, brands, forceAll = false) {
    return api.post('/sync/products/sync', {
      brands: brands,
      force_all: forceAll
    }, {
      params: { store_id: storeId }
    }).then(response => response.data)
  },

  // Создать новый магазин
  createStore(storeData) {
    return api.post('/stores', storeData).then(response => response.data)
  },

  // Обновить настройки магазина
  updateStore(storeId, updateData) {
    return api.put(`/stores/${storeId}`, updateData).then(response => response.data)
  },

  // ============================================================================
  // Переводы (Translations)
  // ============================================================================

  // Получить список поддерживаемых языков
  getSupportedLocales() {
    return api.get('/translations/locales').then(response => response.data)
  },

  // Проверить наличие переводов для магазина
  checkTranslations(storeId) {
    return api.get(`/translations/check/${storeId}`).then(response => response.data)
  },

  // Сгенерировать недостающие переводы через OpenAI
  generateTranslations(storeId, fields = null, preview = false) {
    return api.post(`/translations/generate/${storeId}`, {
      fields: fields,
      preview: preview
    }).then(response => response.data)
  },

  // Сохранить предпросмотренные переводы
  savePreviewTranslations(storeId, translations) {
    return api.post(`/translations/save/${storeId}`, {
      translations: translations
    }).then(response => response.data)
  },

  // Получить список переводов с фильтрацией
  getTranslations(params = {}) {
    return api.get('/translations', { params }).then(response => response.data)
  },

  // Удалить перевод
  deleteTranslation(translationId) {
    return api.delete(`/translations/${translationId}`).then(response => response.data)
  },

  // ============================================================================
  // Синхронизация с Google Sheets
  // ============================================================================

  // Запустить синхронизацию с Google Sheets
  triggerSheetsSync() {
    return api.post('/sheets/sync')
  },

  // Получить статус последней синхронизации
  getSheetsSyncStatus() {
    return api.get('/sheets/sync/status')
  },

  // Проверка: идёт ли синхронизация сейчас
  checkSheetsSyncRunning() {
    return api.get('/sheets/sync/running')
  },

  // Скачать отчет о синхронизации Google Sheets
  downloadSheetsSyncReport(reportId) {
    return api.get(`/sheets/sync/report/${reportId}/download`, {
      responseType: 'blob'
    })
  },

  // Запустить синхронизацию цен Chanel Perfumes
  triggerChanelSync() {
    return api.post('/sheets/chanel/sync')
  },

  // Проверка: идёт ли синхронизация Chanel Perfumes
  checkChanelSyncRunning() {
    return api.get('/sheets/chanel/sync/running')
  },

  // Получить статус последней синхронизации Chanel Perfumes
  getChanelSyncStatus() {
    return api.get('/sheets/chanel/sync/status')
  },

  // Прогресс синхронизации Google Sheets (sheets или chanel)
  getSheetsSyncProgress(syncType) {
    return api.get(`/sheets/sync/progress/${syncType}`)
  },

  // ============================================================================
  // Выгрузки (Data Feeds)
  // ============================================================================

  // Получить список выгрузок
  getDataFeeds() {
    return api.get('/feeds')
  },

  // Создать выгрузку
  createDataFeed(data) {
    return api.post('/feeds', data)
  },

  // Обновить выгрузку
  updateDataFeed(id, data) {
    return api.put(`/feeds/${id}`, data)
  },

  // Удалить выгрузку
  deleteDataFeed(id) {
    return api.delete(`/feeds/${id}`)
  },

  // Перегенерировать токен доступа
  regenerateFeedToken(id) {
    return api.post(`/feeds/${id}/regenerate`)
  },

  // Обновить кэш выгрузки
  refreshFeedCache(id) {
    return api.post(`/feeds/${id}/refresh`)
  },

  // Получить опции фильтров для выгрузок
  getFeedFilterOptions() {
    return api.get('/feeds/filter-options')
  },

  // Shipment Export — генерация таможенного документа
  generateShipmentExport(file, savingsPct = 5.5) {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/shipment/generate?savings_pct=${savingsPct}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 300000
    })
  },

  // ===== Задачи (внутренний задачник) =====
  async getTasks(status = 'active') {
    const response = await api.get('/tasks', { params: { status } })
    return response.data
  },

  async createTask(data) {
    const response = await api.post('/tasks', data)
    return response.data
  },

  async updateTask(id, data) {
    const response = await api.patch(`/tasks/${id}`, data)
    return response.data
  },

  async toggleTask(id, isDone) {
    const body = isDone === undefined ? {} : { is_done: isDone }
    const response = await api.post(`/tasks/${id}/toggle`, body)
    return response.data
  },

  async deleteTask(id) {
    const response = await api.delete(`/tasks/${id}`)
    return response.data
  },

  async uploadTaskImages(id, images) {
    const formData = new FormData()
    images.forEach((image) => formData.append('images', image))
    const response = await api.post(`/tasks/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return response.data
  },

  async deleteTaskImage(id, imageId) {
    const response = await api.delete(`/tasks/${id}/images/${imageId}`)
    return response.data
  },

  // Список пользователей (для выбора исполнителя)
  async getUsers() {
    const response = await api.get('/users')
    return response.data
  }
}
